/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  Box,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { useMemo } from "react";
import { useFormatNumber } from "src/utils/formatNumber";
import { defaultTableSx } from "components/strategyDashboard/tableFormatting";
import { StrategyDashboardDeliveryData } from "src/store/api/generatedApi";
import { Loaded } from "models/loaded";

interface DeliveryRow {
  id: string;
  materialName: string;
  monthExpectedMass: number;
  deliveredMassMtd: number;
  deliveredFraction: number | null;
  expectedFraction: number;
  pacePercent: number | null;
}

export const DeliveryPaceTable = ({
  loadedData,
  showPacePercent,
  showRemainingMass,
  fillHeight = false,
}: {
  loadedData: Loaded<StrategyDashboardDeliveryData>;
  showPacePercent: boolean;
  showRemainingMass: boolean;
  fillHeight?: boolean;
}) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const formatNumber = useFormatNumber();
  const unitsFormatter = useUnitsFormatter(true);

  const rows: DeliveryRow[] = useMemo(() => {
    if (loadedData.status !== "success") return [];

    return loadedData.data.delivery_series.map((item) => ({
      id: item.material_name,
      materialName: item.material_name,
      monthExpectedMass: item.month_expected_mass,
      deliveredMassMtd: item.delivered_mass_mtd,
      deliveredFraction: item.delivered_fraction,
      expectedFraction: item.expected_fraction,
      pacePercent:
        item.delivered_fraction !== null && item.expected_fraction > 0
          ? (item.delivered_fraction / item.expected_fraction) * 100
          : null,
    }));
  }, [loadedData]);

  const totalRow: DeliveryRow = useMemo(() => {
    const totalExpected = rows.reduce((s, r) => s + r.monthExpectedMass, 0);
    const totalDelivered = rows.reduce((s, r) => s + r.deliveredMassMtd, 0);
    const expectedFraction = rows[0]?.expectedFraction ?? 0;
    const totalDeliveredFraction =
      totalExpected > 0 ? totalDelivered / totalExpected : null;
    return {
      id: "totalDeliveries",
      materialName: t("total"),
      monthExpectedMass: totalExpected,
      deliveredMassMtd: totalDelivered,
      deliveredFraction: totalDeliveredFraction,
      expectedFraction,
      pacePercent:
        totalDeliveredFraction !== null && expectedFraction > 0
          ? (totalDeliveredFraction / expectedFraction) * 100
          : null,
    };
  }, [rows, t]);

  const hasExpectedMass = useMemo(() => {
    return rows.some((row) => row.monthExpectedMass > 0);
  }, [rows]);

  const columns: GridColDef<DeliveryRow>[] = useMemo(() => {
    const cols: GridColDef<DeliveryRow>[] = [
      {
        field: "materialName",
        headerName: t("material"),
        flex: 1.2,
        minWidth: 120,
      },
    ];

    if (hasExpectedMass) {
      cols.push({
        field: "monthExpectedMass",
        headerName: `${t("monthExpected")}${unitsFormatter("mass")}`,
        type: "number",
        flex: 1,
        minWidth: 100,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value: number) =>
          value === 0 ? "-" : formatNumber(0, true)(value),
      });
    }

    cols.push({
      field: "deliveredMassMtd",
      headerName: `${t("deliveredMtd")}${unitsFormatter("mass")}`,
      type: "number",
      flex: 1,
      minWidth: 100,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value: number) => formatNumber(0, true)(value),
    });

    if (hasExpectedMass && showRemainingMass) {
      cols.push({
        field: "remainingMass",
        headerName: `${t("remaining")}${unitsFormatter("mass")}`,
        type: "number",
        flex: 1,
        minWidth: 100,
        align: "right",
        headerAlign: "right",
        valueGetter: (_value: unknown, row: DeliveryRow) =>
          row.monthExpectedMass === 0
            ? null
            : row.monthExpectedMass - row.deliveredMassMtd,
        valueFormatter: (value: number | null) =>
          value === null ? "-" : formatNumber(0, true)(value),
      });
    }

    if (hasExpectedMass) {
      cols.push({
        field: "deliveryPace",
        headerName: t("progress"),
        type: "number",
        flex: 0.5,
        minWidth: 80,
        align: "right",
        headerAlign: "right",
        valueGetter: (_value: unknown, row: DeliveryRow) => {
          if (row.deliveredFraction === null) {
            return null;
          }
          return `${formatNumber(0, true)(row.deliveredFraction * 100)} %`;
        },
      });

      if (showPacePercent) {
        cols.push({
          field: "pacePercent",
          headerName: `${t("pace")} %`,
          type: "number",
          flex: 0.5,
          minWidth: 80,
          align: "right",
          headerAlign: "right",
          valueFormatter: (value: number | null) =>
            value !== null ? `${formatNumber(0, true)(value)} %` : "-",
        });
      }

      const expectedFrac = rows[0]?.expectedFraction ?? 0;

      cols.push({
        field: "deliveredFraction",
        headerName: t("pace"),
        type: "number",
        flex: 1.2,
        minWidth: 120,
        align: "right",
        headerAlign: "right",
        cellClassName: "coloured-cell",
        renderHeader: () => (
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Typography variant="body2" color={theme.palette.text.secondary}>
              {t("pace")}
            </Typography>
            <Tooltip title={t("deliveriesPaceTooltip")} arrow placement="top">
              <HelpOutlineIcon
                sx={{
                  fontSize: 16,
                  color: "text.disabled",
                  cursor: "help",
                }}
              />
            </Tooltip>
          </Stack>
        ),
        renderCell: (
          params: GridRenderCellParams<DeliveryRow, number | null>
        ) => {
          const fraction = params.value;
          const percentage = Math.min((fraction ?? 0) * 100, 100);
          const rowIndex = rows.findIndex((r) => r.id === params.row.id);
          const isInLastTwoRows = rowIndex >= rows.length - 2;
          const showTargetLabel =
            rowIndex >= 2 && (rowIndex - 2) % 4 === 0 && !isInLastTwoRows;
          return (
            <ProgressBarCell
              percentage={percentage}
              color={theme.palette.primary.main}
              targetFraction={expectedFrac}
              showLabel={showTargetLabel}
            />
          );
        },
      });
    }

    return cols;
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- unitsFormatter is a hook-returned function with potentially unstable identity; columns should only rebuild when the listed deps change
  }, [
    t,
    hasExpectedMass,
    formatNumber,
    theme,
    rows,
    showPacePercent,
    showRemainingMass,
  ]);

  const isFetching =
    loadedData.status === "success" ? loadedData.fetching : false;
  const isLoading =
    loadedData.status === "loading" || loadedData.status === "waiting";

  if (isLoading || isFetching) {
    return <Skeleton variant="rectangular" height={250} />;
  }

  if (loadedData.status !== "success" || rows.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          {t("noData")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: fillHeight ? "100%" : undefined,
      }}
    >
      <HotkeyCompliantDataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        pinnedRows={{ bottom: [totalRow] }}
        sx={{
          ...defaultTableSx,
          mt: 0,
          "& .coloured-cell": {
            overflow: "visible",
            padding: 0,
          },
          "& .total-row .MuiDataGrid-cell": {
            fontWeight: "fontWeightBold",
          },
        }}
        getRowClassName={(params) =>
          params.row.id === "totalDeliveries" ? "total-row" : ""
        }
        initialState={{
          sorting: { sortModel: [{ field: "materialName", sort: "asc" }] },
        }}
      />
    </Box>
  );
};

const ProgressBarCell = ({
  percentage,
  color,
  targetFraction,
  showLabel = false,
}: {
  percentage: number;
  color: string;
  targetFraction?: number;
  showLabel?: boolean;
}) => {
  const theme = useTheme();
  const { t } = useTenantTranslation();

  return (
    <Box
      style={{
        position: "relative",
        width: "100%",
        height: 26,
        borderRadius: 2,
        overflow: "visible",
      }}
    >
      <Box
        style={{
          width: `${percentage}%`,
          backgroundColor: color,
          opacity: 0.4,
          height: "100%",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      />
      {targetFraction !== undefined && targetFraction > 0 && (
        <>
          <Box
            style={{
              position: "absolute",
              left: `${targetFraction * 100}%`,
              top: 0,
              bottom: 0,
              width: 2,
              backgroundColor: theme.palette.error.main,
              transform: "translateX(-50%)",
              zIndex: 1,
              opacity: 0.9,
            }}
          />
          {showLabel ? (
            <Typography
              variant="body2"
              sx={{
                position: "absolute",
                left: `${targetFraction * 100}%`,
                top: "50%",
                transform:
                  targetFraction > 0.85
                    ? "translateX(calc(-100% - 16px)) translateY(-65%) rotate(90deg)"
                    : "translateX(16px) translateY(-65%) rotate(90deg)",
                transformOrigin:
                  targetFraction > 0.85 ? "right center" : "left center",
                color: theme.palette.error.main,
                whiteSpace: "nowrap",
                fontSize: "0.8rem",
                fontWeight: "fontWeightMedium",
                pointerEvents: "none",
                zIndex: 1,
                opacity: 0.9,
              }}
            >
              {t("target")}
            </Typography>
          ) : null}
        </>
      )}
    </Box>
  );
};
